import React from "react";
import { ShieldCheck } from "lucide-react";
import { UseFormReturn } from "react-hook-form";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { CreateStoreFormData } from "./createStoreSchema";
import { useTranslation } from "react-i18next";

interface B2BRequirementsStepProps {
  form: UseFormReturn<CreateStoreFormData>;
}

export const B2BRequirementsStep: React.FC<B2BRequirementsStepProps> = ({
  form,
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-5 w-5" />
        <h3 className="text-lg font-medium">
          {t("b2bRequirementsStep.b2bRequirements")}
        </h3>
      </div>

      <Separator />

      <FormField
        control={form.control}
        name="isB2B"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>
                {t("b2bRequirementsStep.acceptB2BInvoicing")}
              </FormLabel>
              <FormDescription>
                {t("b2bRequirementsStep.acceptB2BInvoicingDescription")}
              </FormDescription>
            </div>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="industryType"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t("b2bRequirementsStep.industryTypes.title")}
            </FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue
                    placeholder={t("b2bRequirementsStep.industryTypes.title")}
                  />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="restaurant">
                  {t("b2bRequirementsStep.industryTypes.restaurant")}
                </SelectItem>
                <SelectItem value="cafe">
                  {t("b2bRequirementsStep.industryTypes.cafe")}
                </SelectItem>
                <SelectItem value="bar">
                  {t("b2bRequirementsStep.industryTypes.bar")}
                </SelectItem>
                <SelectItem value="nightclub">
                  {t("b2bRequirementsStep.industryTypes.nightclub")}
                </SelectItem>
                <SelectItem value="hotel">
                  {t("b2bRequirementsStep.industryTypes.hotel")}
                </SelectItem>
                <SelectItem value="spa">
                  {t("b2bRequirementsStep.industryTypes.spa")}
                </SelectItem>
                <SelectItem value="salon">
                  {t("b2bRequirementsStep.industryTypes.salon")}
                </SelectItem>
                <SelectItem value="tattoo">
                  {t("b2bRequirementsStep.industryTypes.tattoo")}
                </SelectItem>
                <SelectItem value="massage_therapy">
                  {t("b2bRequirementsStep.industryTypes.massage_therapy")}
                </SelectItem>
                <SelectItem value="catering">
                  {t("b2bRequirementsStep.industryTypes.catering")}
                </SelectItem>
                <SelectItem value="other">
                  {t("b2bRequirementsStep.industryTypes.other")}
                </SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="taxExemptionCertificate"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>
                {t("b2bRequirementsStep.taxExemptionCertificate")}
              </FormLabel>
              <FormDescription>
                {t("b2bRequirementsStep.taxExemptionCertificateDescription")}
              </FormDescription>
            </div>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="gdprCompliance"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>{t("b2bRequirementsStep.gdprCompliance")}</FormLabel>
              <FormDescription>
                {t("b2bRequirementsStep.gdprComplianceDescription")}
              </FormDescription>
            </div>
          </FormItem>
        )}
      />
    </div>
  );
};
